import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { documentationRootGuid } from '@constants'
import { v4 } from 'uuid'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Form, TextField } from '@ui/forms'
import SubmitButton from '@ui/forms/SubmitButton'
import { useSnackbar } from '@ui/snackbar'
import { getProjectFiles } from '@queries'
import { createProjectFolderItem } from '@mutations'

interface Props {
  open: boolean
  onClose: () => void
  parentGuid: string
  onCreateFolder?: (folderId: string) => void
}

const AddFolderModal = ({ open, onClose, parentGuid, onCreateFolder }: Props) => {
  const { projectId } = useParams()

  const queryClient = useQueryClient()

  const { enqueueSnackbar } = useSnackbar()

  const { mutate, isLoading } = useMutation(
    async (name: string) => {
      const data = {
        folder_name: name,
        parent_guid: parentGuid,
      }
      return await createProjectFolderItem.request(projectId, documentationRootGuid, data)
    },
    {
      onSuccess: (data: SectionItem) => {
        enqueueSnackbar('Ordner erstellt', { variant: 'success' })

        const getProjectFilesKey = getProjectFiles.getKey(projectId, documentationRootGuid)

        queryClient.setQueryData<FilesResult>(getProjectFilesKey, prev => {
          if (!prev) return undefined

          return {
            ...prev,
            sections: [...prev.sections, data],
          }
        })
        onClose()
        onCreateFolder?.(data.guid)
        setId(v4())
      },
      onError: () => {
        enqueueSnackbar('Fehler beim Erstellen des Ordners', { variant: 'error' })
      },
    },
  )

  const [id, setId] = useState<string>(v4())

  return (
    <Dialog open={open} onClose={onClose}>
      <Form key={id} onSubmit={data => mutate(data.name)} defaultValues={{ name: '' }}>
        <DialogTitle>Abschnitt hinzufügen</DialogTitle>
        <DialogContent style={{ minWidth: 400 }}>
          <TextField name="name" placeholder="Name" />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Schließen</Button>
          <SubmitButton loading={isLoading} text="Erstellen" fullWidth={false} />
        </DialogActions>
      </Form>
    </Dialog>
  )
}

export default AddFolderModal
